<div class="documentation-page">
  <div class="documentation-container">
    <!-- Loading State -->
    @if (loading()) {
      <div class="loading-container">
        <p-progressSpinner></p-progressSpinner>
      </div>
    }

    <!-- Content -->
    @if (!loading()) {
      <div class="documentation-layout">
        <!-- Left Sidebar - File Explorer -->
        <div class="explorer-panel">
          <div class="explorer-header">
            <h3>{{ 'Documentation' | translate }}</h3>
          </div>
          <div class="explorer-content">
            @if (blocks().length === 0) {
              <div class="empty-state">
                <p>{{ 'No documentation available' | translate }}</p>
              </div>
            } @else {
              @for (block of blocks(); track block.id) {
                <div class="block-item" [class.expanded]="isBlockExpanded(block.id)">
                  <div class="block-header" (click)="toggleBlock(block.id)">
                    <span class="block-title">{{ getNameForLanguage(block) }}</span>
                    <i class="pi" [class.pi-chevron-down]="!isBlockExpanded(block.id)" [class.pi-chevron-up]="isBlockExpanded(block.id)"></i>
                  </div>
                  @if (isBlockExpanded(block.id)) {
                    <div class="block-content">
                      <!-- Documents in this block -->
                      @if (block.documents && block.documents.length > 0) {
                        <div class="document-list">
                          @for (doc of block.documents; track doc.id) {
                            <div 
                              class="document-item"
                              [class.active]="selectedDocument()?.id === doc.id"
                              (click)="selectDocument(doc)">
                              <div class="document-name">
                                <i class="pi pi-file" style="margin-right: 8px;"></i>
                                {{ getNameForLanguage(doc) }}
                              </div>
                              @if (doc.fileSizeString) {
                                <div class="document-size">{{ doc.fileSizeString }}</div>
                              }
                            </div>
                          }
                        </div>
                      }

                      <!-- Child blocks -->
                      @if (block.children && block.children.length > 0) {
                        <div class="child-blocks">
                          @for (childBlock of block.children; track childBlock.id) {
                            <div class="child-block-item" [class.expanded]="isChildBlockExpanded(childBlock.id)">
                              <div class="child-block-header" (click)="toggleChildBlock(childBlock.id)">
                                <span class="block-title">{{ getNameForLanguage(childBlock) }}</span>
                                <i class="pi" [class.pi-chevron-down]="!isChildBlockExpanded(childBlock.id)" [class.pi-chevron-up]="isChildBlockExpanded(childBlock.id)"></i>
                              </div>
                              @if (isChildBlockExpanded(childBlock.id)) {
                                <div class="child-block-content">
                                  @if (childBlock.documents && childBlock.documents.length > 0) {
                                    <div class="document-list">
                                      @for (doc of childBlock.documents; track doc.id) {
                                        <div 
                                          class="document-item"
                                          [class.active]="selectedDocument()?.id === doc.id"
                                          (click)="selectDocument(doc)">
                                          <div class="document-name">
                                            <i class="pi pi-file" style="margin-right: 8px;"></i>
                                            {{ getNameForLanguage(doc) }}
                                          </div>
                                          @if (doc.fileSizeString) {
                                            <div class="document-size">{{ doc.fileSizeString }}</div>
                                          }
                                        </div>
                                      }
                                    </div>
                                  }
                                </div>
                              }
                            </div>
                          }
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            }
          </div>
        </div>

        <!-- Right Panel - Preview -->
        <div class="preview-panel">
          @if (!selectedDocument()) {
            <div class="preview-empty">
              <div class="empty-content">
                <i class="pi pi-file" style="font-size: 48px; color: #ccc;"></i>
                <p>{{ 'Select a document to view' | translate }}</p>
              </div>
            </div>
          } @else {
            <div class="preview-content">
              <div class="preview-header">
                <h3>{{ getNameForLanguage(selectedDocument()!) }}</h3>
                <button
                  pButton
                  icon="pi pi-download"
                  [label]="'Download' | translate"
                  class="p-button-secondary p-button-sm"
                  (click)="downloadFile(selectedDocument()!)"
                  [disabled]="loadingDocument()">
                </button>
              </div>

              <div class="preview-body" [class.loading]="loadingDocument()">
                @if (loadingDocument()) {
                  <div class="document-loading-overlay">
                    <p-progressSpinner></p-progressSpinner>
                    <p class="loading-text">{{ 'Loading document...' | translate }}</p>
                  </div>
                }
                @if (fileType() && canPreview(fileType())) {
                  <!-- PDF Preview -->
                  @if (isPdfFile(fileType())) {
                    <iframe
                      [src]="fileUrl()"
                      class="preview-iframe"
                      type="application/pdf">
                    </iframe>
                  }

                  <!-- Image Preview -->
                  @if (isImageFile(fileType())) {
                    <div class="preview-image-container">
                      <img [src]="fileUrl()" [alt]="getNameForLanguage(selectedDocument()!)" class="preview-image">
                    </div>
                  }

                  <!-- Markdown Preview -->
                  @if (isMarkdownFile(fileType()) && fileContent()) {
                    <div class="preview-markdown">
                      <div class="markdown-content" #markdownContent [innerHTML]="fileContent() | markdown"></div>
                    </div>
                  }

                  <!-- Text Preview -->
                  @if (isTextFile(fileType()) && fileContent()) {
                    <div class="preview-text">
                      <pre class="text-content">{{ fileContent() }}</pre>
                    </div>
                  }
                } @else {
                  <!-- Other file types - show download link -->
                  <div class="preview-other">
                    <div class="file-info">
                      <i class="pi pi-file" style="font-size: 48px; color: #ccc;"></i>
                      <p>{{ 'File preview is not available for this file type' | translate }}</p>
                      <p class="file-name">{{ selectedDocument()?.fileName }}</p>
                      @if (selectedDocument()?.fileSizeString) {
                        <p class="file-size">{{ selectedDocument()?.fileSizeString }}</p>
                      }
                    </div>
                    <button
                      pButton
                      icon="pi pi-download"
                      [label]="'Download File' | translate"
                      class="p-button-primary"
                      (click)="downloadFile(selectedDocument()!)">
                    </button>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>
    }
  </div>
</div>

